/** Pluma y resaltador: captura de tinta con presión y suavizado en vivo. */
import { getStrokeOutline, outlineToPath, pencilPressure, type InkPoint } from '../lib/freehand.ts'
import type { AppContext, PointerInfo, Tool } from './types.ts'
import { commitStroke } from './commit-stroke.ts'
import { displayInk } from '../canvas/render-shape.ts'
import { PRESSURE_THINNING } from '../state/store.ts'

export class DrawTool implements Tool {
  readonly id: 'draw' | 'highlighter'
  readonly cursor = 'crosshair'
  private ctx: AppContext
  private points: InkPoint[] = []
  private drawing = false
  private removePainter: (() => void) | null = null
  private lastPoint: InkPoint | null = null
  /** el trazo actual trae presión real (lápiz) y no el valor neutro del ratón */
  private sawPressure = false

  constructor(ctx: AppContext, id: 'draw' | 'highlighter') {
    this.ctx = ctx
    this.id = id
  }

  private get color(): string {
    const { style } = this.ctx.store
    return this.id === 'draw' ? style.inkColor : style.highlighterColor
  }

  private get size(): number {
    const { style } = this.ctx.store
    return this.id === 'draw' ? style.inkSize : style.highlighterSize
  }

  /** Cuánto modula la presión el grosor con el ajuste actual. */
  private get thinning(): number {
    if (this.id === 'highlighter') return 0
    return PRESSURE_THINNING[this.ctx.store.style.penPressure] ?? 0.55
  }

  onActivate(): void {
    this.removePainter = this.ctx.renderer.addPainter((c) => this.paintStroke(c))
  }

  onDeactivate(): void {
    this.removePainter?.()
    this.removePainter = null
    this.drawing = false
    this.points = []
  }

  onPointerDown(p: PointerInfo): void {
    if (p.button !== 0 && p.button !== -1) return
    this.drawing = true
    this.sawPressure = p.pointerType === 'pen'
    this.points = [{ x: p.x, y: p.y, p: normalizePressure(p) }]
    this.lastPoint = this.points[0]
    this.ctx.renderer.invalidateOverlay()
  }

  onPointerMove(p: PointerInfo): void {
    if (!this.drawing) return
    const point: InkPoint = { x: p.x, y: p.y, p: normalizePressure(p) }
    if (p.pointerType === 'pen') this.sawPressure = true
    const last = this.lastPoint
    // Descarta micro-movimientos: menos puntos = trazo más limpio y ligero.
    if (last && Math.hypot(point.x - last.x, point.y - last.y) < 0.6 / this.ctx.store.camera.z) return
    this.points.push(point)
    this.lastPoint = point
    this.ctx.renderer.invalidateOverlay()
  }

  onPointerUp(): void {
    if (!this.drawing) return
    this.drawing = false
    const raw = this.points
    this.points = []
    this.ctx.renderer.invalidateOverlay()
    commitStroke(this.ctx, raw, {
      color: this.color,
      size: this.size,
      highlighter: this.id === 'highlighter',
      thinning: this.thinning,
    })
  }

  /**
   * Cancelar guarda el trazo igual que soltarlo: iPadOS cancela el gesto por
   * rutina (palma, gestos del sistema) y que un trazo desaparezca de la
   * pantalla es lo peor que puede pasar. Con menos de 2 puntos no hay trazo.
   */
  onCancel(): void {
    if (!this.drawing) return
    this.drawing = false
    const raw = this.points
    this.points = []
    this.ctx.renderer.invalidateOverlay()
    if (raw.length < 2) return
    commitStroke(this.ctx, raw, {
      color: this.color,
      size: this.size,
      highlighter: this.id === 'highlighter',
      thinning: this.thinning,
    })
  }

  private paintStroke(c: CanvasRenderingContext2D): void {
    if (this.points.length === 0) return
    const isHighlighter = this.id === 'highlighter'
    const outline = getStrokeOutline(this.points, {
      size: this.size,
      last: false,
      flat: isHighlighter,
      thinning: isHighlighter ? 0 : this.thinning,
      streamline: 0.5,
    })
    if (outline.length < 4) return
    c.save()
    if (isHighlighter) {
      c.globalAlpha = this.ctx.store.darkMode ? 0.45 : 0.38
      c.globalCompositeOperation = this.ctx.store.darkMode ? 'screen' : 'multiply'
    }
    c.fillStyle = isHighlighter ? this.color : displayInk(this.color)
    c.fill(outlineToPath(outline))
    c.restore()
  }
}

/** Normaliza la presión: los ratones reportan 0 o 0.5; el lápiz, su fuerza real con la curva natural. */
function normalizePressure(p: PointerInfo): number {
  if (p.pointerType === 'pen' && p.pressure > 0) return pencilPressure(p.pressure)
  if (p.pointerType === 'touch' && p.pressure > 0 && p.pressure !== 0.5) return Math.min(1, p.pressure)
  return 0.5
}
